import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ConfigProvider, theme, Result, Button } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import ErrorBoundary from './components/ErrorBoundary';
import { isTokenValid, getStoredUser, clearAuth } from './utils/auth';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/login/LoginPage';
import Dashboard from './pages/dashboard/Dashboard';
import Monitoring from './pages/microgrid/monitoring/Monitoring';
import VideoMonitoring from './pages/microgrid/video/VideoMonitoring';
import VideoCalibration from './pages/microgrid/video/VideoCalibration';
import Photovoltaic from './pages/microgrid/photovoltaic/Photovoltaic';
import Storage from './pages/microgrid/storage/Storage';
import Charging from './pages/microgrid/charging/Charging';
import Energy from './pages/microgrid/energy/Energy';
import Demand from './pages/microgrid/demand/Demand';
import Strategy from './pages/microgrid/strategy/Strategy';
import Alarms from './pages/microgrid/alarms/Alarms';
import WorkOrders from './pages/microgrid/workorders/WorkOrders';
import Carbon from './pages/microgrid/carbon/Carbon';
import EdgeCollector from './pages/microgrid/edge/EdgeCollector';
import VppResources from './pages/vpp/resources/VppResources';
import VppDispatch from './pages/vpp/dispatch/VppDispatch';
import VppMarket from './pages/vpp/market/VppMarket';
import VppDemand from './pages/vpp/demand/VppDemand';
import VppForecast from './pages/vpp/forecast/VppForecast';
import ReportCenter from './pages/reports/ReportCenter';
import VppSettlement from './pages/vpp/settlement/VppSettlement';
import VppTradeLoop from './pages/vpp/trade/VppTradeLoop';
import VppReview from './pages/vpp/review/VppReview';
import VppQuotation from './pages/vpp/quotation/VppQuotation';
import VppCustomers from './pages/vpp/customers/VppCustomers';
import DigitalTwin from './pages/microgrid/twin/DigitalTwin';
import TenantAdmin from './pages/admin/TenantAdmin';
import UsersAdmin from './pages/admin/Users';
import Devices from './pages/devices/Devices';
import Audit from './pages/audit/Audit';
import BigScreen from './pages/bigscreen/BigScreen';
import MobileLayout from './layouts/MobileLayout';
import MobileHome from './pages/mobile/MobileHome';
import MobileAlarms from './pages/mobile/MobileAlarms';
import MobileWorkOrders from './pages/mobile/MobileWorkOrders';
import MobileInspection from './pages/mobile/MobileInspection';

// 登录守卫：token 缺失、解析失败或已过期时清理登录态并跳回登录页
function RequireAuth({ children }) {
  const token = localStorage.getItem('qhy_token');
  const location = useLocation();
  if (!isTokenValid(token)) {
    clearAuth();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

// 管理员守卫：非 admin 访问管理页面时显示 403
function RequireAdmin({ children }) {
  const user = getStoredUser();
  if (user.role !== 'admin') {
    return (
      <Result
        status="403"
        title="403"
        subTitle="暂无权限访问该页面"
        extra={<Link to="/dashboard"><Button type="primary">返回首页</Button></Link>}
      />
    );
  }
  return children;
}

export default function App() {
  return (
    <ConfigProvider locale={zhCN} theme={{ algorithm: theme.defaultAlgorithm }}>
      <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/bigscreen" element={<RequireAuth><BigScreen /></RequireAuth>} />
        <Route path="/m" element={<RequireAuth><MobileLayout /></RequireAuth>}>
          <Route index element={<MobileHome />} />
          <Route path="alarms" element={<MobileAlarms />} />
          <Route path="tasks" element={<MobileWorkOrders />} />
          <Route path="tasks/:id" element={<MobileWorkOrders />} />
          <Route path="inspection" element={<MobileInspection />} />
        </Route>
        <Route path="/" element={<RequireAuth><MainLayout /></RequireAuth>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          {/* 微电网 */}
          <Route path="monitoring" element={<Monitoring />} />
          <Route path="video-monitoring" element={<VideoMonitoring />} />
          <Route path="video-calibration" element={<VideoCalibration />} />
          <Route path="photovoltaic" element={<Photovoltaic />} />
          <Route path="storage" element={<Storage />} />
          <Route path="charging" element={<Charging />} />
          <Route path="energy" element={<Energy />} />
          <Route path="demand" element={<Demand />} />
          <Route path="strategy" element={<Strategy />} />
          <Route path="carbon" element={<Carbon />} />
          <Route path="edge-collector" element={<EdgeCollector />} />
          <Route path="digital-twin" element={<DigitalTwin />} />
          <Route path="alarms" element={<Alarms />} />
          <Route path="work-orders" element={<WorkOrders />} />
          {/* VPP */}
          <Route path="vpp/trade-loop" element={<VppTradeLoop />} />
          <Route path="vpp/quotation" element={<VppQuotation />} />
          <Route path="vpp/resources" element={<VppResources />} />
          <Route path="vpp/dispatch" element={<VppDispatch />} />
          <Route path="vpp/market" element={<VppMarket />} />
          <Route path="vpp/demand" element={<VppDemand />} />
          <Route path="vpp/forecast" element={<VppForecast />} />
          <Route path="vpp/review" element={<VppReview />} />
          <Route path="vpp/settlement" element={<VppSettlement />} />
          <Route path="vpp/customers" element={<VppCustomers />} />
          {/* 设备 */}
          <Route path="devices" element={<Devices />} />
          <Route path="reports" element={<ReportCenter />} />
          <Route path="audit" element={<RequireAdmin><Audit /></RequireAdmin>} />
          <Route path="tenants" element={<RequireAdmin><TenantAdmin /></RequireAdmin>} />
          <Route path="users" element={<RequireAdmin><UsersAdmin /></RequireAdmin>} />
        </Route>
      </Routes>
      </ErrorBoundary>
    </ConfigProvider>
  );
}
